import { Link } from 'react-router-dom';
import { useFiles } from '../hooks/useFiles';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FileStatsCards } from '../components/files/FileStatsCards';
import { FileFilters } from '../components/files/FileFilters';
import { FileListItem } from '../components/files/FileListItem';
import { useDevice } from '../contexts/DeviceContext';

export default function Files() {
  const {
    files,
    loading,
    totalCount,
    stats,
    toasts,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    rangeStart,
    rangeEnd,
    searchTerm,
    setSearch,
    fileTypeFilter,
    setTypeFilter,
    sortBy,
    setSortBy,
    clearFilters,
    deletingFileId,
    pendingDelete,
    setPendingDelete,
    handleDownload,
    handleDelete,
    executeDelete,
    canDeleteFile,
  } = useFiles();

  const { isMobile } = useDevice();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-navy mb-1">📁 All Files Dashboard</h2>
        <p className="text-sm text-steel hidden md:block">Browse and manage all files across your training sessions</p>
      </div>

      <FileStatsCards totalCount={totalCount} stats={stats} />

      <FileFilters
        searchTerm={searchTerm}
        setSearch={setSearch}
        fileTypeFilter={fileTypeFilter}
        setTypeFilter={setTypeFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        clearFilters={clearFilters}
      />

      {/* Upload info */}
      <div className="bg-blue-50 border-l-4 border-orca-elevate rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2">
          <span className="text-lg flex-shrink-0">ℹ️</span>
          <p className="text-sm text-steel">
            Upload files via{' '}
            <Link to="/sessions" className="text-orca-elevate hover:text-orca-elevate/80 font-medium">
              Training Sessions
            </Link>
            {' '}— open a session and upload directly there.
          </p>
        </div>
      </div>

      {/* Files list */}
      <div className="bg-white rounded-lg shadow-md p-3 md:p-6">
        {/* List header */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-navy">
            {totalCount === 0
              ? 'No files'
              : `${rangeStart}–${rangeEnd} of ${totalCount} ${totalCount === 1 ? 'file' : 'files'}`}
          </p>
          {!isMobile && (
            <div className="flex items-center gap-2 text-sm text-steel">
              <span>Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 bg-white border border-steel/30 rounded-md text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate" />
            <p className="mt-2 text-sm text-steel">Loading files…</p>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">📁</div>
            {!searchTerm && fileTypeFilter === 'all' ? (
              <>
                <p className="text-steel mb-2 text-sm">No files found</p>
                <Link to="/sessions" className="text-sm text-orca-elevate hover:text-orca-elevate/80 font-medium">
                  Go to Training Sessions
                </Link>
              </>
            ) : (
              <>
                <p className="text-steel mb-2 text-sm">No files match your filters</p>
                <button onClick={clearFilters} className="text-sm text-orca-elevate hover:text-orca-elevate/80 font-medium">
                  Clear filters
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2 md:space-y-3">
            {files.map((file) => (
              <FileListItem
                key={file.id}
                file={file}
                canDelete={canDeleteFile(file)}
                isDeleting={deletingFileId === file.id}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-steel/20">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-navy border border-steel/30 rounded-md hover:bg-ice disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>

            {/* Mobile: simple page indicator */}
            <span className="md:hidden text-sm text-steel">
              {page} / {totalPages}
            </span>

            {/* Desktop: full page numbers */}
            <div className="hidden md:flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-steel">…</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setPage(item as number)}
                      className={`w-9 h-9 text-sm rounded-md transition-colors ${
                        page === item
                          ? 'bg-orca-elevate text-white font-semibold'
                          : 'text-navy hover:bg-ice border border-steel/30'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
            </div>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-navy border border-steel/30 rounded-md hover:bg-ice disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete file"
        description={`Are you sure you want to delete "${pendingDelete?.file_name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isConfirming={deletingFileId === pendingDelete?.id}
        onConfirm={executeDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Toasts — raised above bottom nav on mobile */}
      {toasts.length > 0 && (
        <div className="fixed bottom-20 md:bottom-4 right-4 z-[80] flex flex-col gap-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${
                toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
              }`}
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
